/**
 * Planner Agent Parameter Schema
 */

import { z } from "zod";
import { type Plan } from "./tools/types";

/**
 * Parameters for the planner agent
 * 
 * Per Notion documentation: https://www.notion.so/usecarousel/Planner-Agent-2bd1f0db4aec81eabc9ed5db68c3e0be
 */
export const PlannerParamsSchema = z.object({
    initialPrompt: z.string()
        .describe("User's natural language description of desired presentation"),
    uiDescription: z.string()
        .describe("Single-line UI description for top-of-card agent status display."),
    uiDescriptions: z.array(z.string())
        .optional()
        .describe("Optional list of UI description lines for the top section of agent-as-tool cards."),
    slideCount: z.number()
        .int()
        .min(10, "Slide count must be at least 10")
        .max(100, "Slide count must be at most 100")
        .describe("Target number of slides for the presentation (10-100)"),
    jobId: z.string()
        .describe("Unique identifier for this planning job"),
    threadId: z.string()
        .optional()
        .describe("Resume existing conversation thread (optional)"),
    currentPlan: z.any()
        .optional()
        .describe("Current plan if editing an existing plan"),
    styleGuide: z.object({
        type: z.enum(['live', 'uploaded']),
        documentId: z.string()
            .describe("Deck ID from DB"),
        path: z.string()
            .optional()
            .describe("File path if uploaded")
    }).describe("Reference presentation for layout/styling inspiration"),
    parsedStyleGuide: z.object({
        slides: z.array(z.object({
            slide_index: z.number(),
            layout_id: z.string().nullable().optional(),
            layout_name: z.string().nullable().optional(),
            shapes: z.array(z.object({
                shape_id: z.string(),
                shape_type: z.string(),
                position: z.object({
                    top: z.number(),
                    left: z.number(),
                    width: z.number(),
                    height: z.number()
                }),
                text: z.string().nullable().optional()
            }))
        })),
        layouts_used: z.array(z.object({
            layout_id: z.string(),
            layout_name: z.string().nullable().optional(),
            count: z.number()
        })),
        sections: z.array(z.object({
            name: z.string(),
            start_index: z.number(),
            end_index: z.number()
        }))
    }).optional().describe("Parsed style guide content - layout and text info from the reference presentation")
}).catchall(z.unknown()); // Allow additional properties for flexibility

/**
 * Response from the planner agent
 */
export interface PlannerResponse {
    plan?: Plan;
    message: string;
    action: 'plan_created' | 'plan_updated' | 'conversation' | 'error';
    metadata?: {
        totalSlides?: number;
        totalSections?: number;
        changes?: {
            added: string[];
            removed: string[];
            modified: string[];
            reordered: string[];
        };
    };
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export type PlannerParams = z.infer<typeof PlannerParamsSchema>;

